import { Component, signal, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
import { ImgFallbackDirective } from '../../shared/directives/img-fallback.directive';
@Component({
  selector: 'app-expertise',
  standalone: true,
  imports: [RouterLink, ScrollAnimateDirective, ImgFallbackDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Category tabs -->
    <section class="py-6 bg-white border-b border-surface-100 sticky top-[68px] z-30">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="flex flex-wrap gap-2">
          @for (cat of page.categories; track cat) {
            <button (click)="activeCategory.set(cat)"
                    class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    [class]="activeCategory() === cat ? 'bg-brand-600 text-white' : 'bg-surface-100 text-surface-500 hover:bg-surface-200'">
              {{ cat }}
            </button>
          }
        </div>
      </div>
    </section>

    <!-- Gallery -->
    <section class="py-16 md:py-24 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          @for (item of filteredItems(); track item.title; let i = $index) {
            <a [routerLink]="'/expertise/' + item.slug" appScrollAnimate data-animate
               class="group aspect-square rounded-xl overflow-hidden cursor-pointer relative">
              <img [src]="item.image" [alt]="item.title" class="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/70 transition-colors duration-300"></div>
              <div class="absolute bottom-0 left-0 right-0 p-4">
                <h3 class="font-heading text-sm font-bold text-white mb-0.5">{{ item.title }}</h3>
                <span class="text-xs text-white/70">{{ item.category }}</span>
              </div>
            </a>
          }
        </div>
      </div>
    </section>

    <!-- Vendor Partners -->
    <section class="py-16 bg-surface-50 border-t border-surface-100">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-10" appScrollAnimate data-animate>
          <div class="section-label justify-center">Trusted Partners</div>
          <h2 class="font-heading text-2xl font-bold text-surface-900 tracking-tight">We Work with the Best Brands</h2>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
          @for (v of page.vendors; track v.name) {
            <div class="flex flex-col items-center gap-2.5 opacity-50 hover:opacity-100 transition-opacity duration-300">
              <img [src]="v.logo" [alt]="v.name" [appImgFallback]="v.name" class="w-20 h-20 rounded-xl object-cover" />
              <span class="text-xs font-medium text-surface-400">{{ v.name }}</span>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-16 bg-brand-600">
      <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Ready for Your Project?</h2>
        <a routerLink="/contact" class="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors">
          Get Free Estimate
        </a>
      </div>
    </section>
  `,
})
export class ExpertiseComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.expertise!;
  activeCategory = signal('All');

  filteredItems = () => {
    const cat = this.activeCategory();
    return cat === 'All' ? this.page.items : this.page.items.filter(i => i.category === cat);
  };

  ngOnInit(): void {
    this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Expertise', url: '/expertise' },
    ]));
  }
}
